import { ActivatedRoute, ActivatedRouteSnapshot, Route } from "@angular/router";
import { ProximityRightEnum } from "@models";
import * as _ from "lodash-es";
import { ToastrService } from "ngx-toastr";
import { MenuItem } from "primeng/api";
import { AuthUser, Roles } from "../services/auth.service";

/** map of route path to rights that may access the path, e.g. '/suppliers/:supplierId/users' -> [ProximityRightEnum.UserUpdate] */
export declare type RightsMap = { [path: string]: ProximityRightEnum[] };

export class RouteFns {

  /** Determine if user has access to the route.  Show a toast if not. */
  static checkRoutePermissions(route: ActivatedRouteSnapshot, defaultRights: ProximityRightEnum[], user: AuthUser, toastr: ToastrService): boolean {
    if (user?.hasRole(Roles.GlobalAdmin)) {
      return true;
    }

    // If route has rights defined, check that user has the rights.  Else check against the default rights
    const routeRights = route.data?.['rights'] as ProximityRightEnum[] || defaultRights;
    if (!routeRights) {
      return true;
    }

    if (user.hasRight(...routeRights)) {
      return true;
    }
    const perms = routeRights.map(r => _.startCase(ProximityRightEnum[r])).join(' or ');
    toastr.error(`${perms} permission is required for this route`, 'Permission');
    return false;

  }

  /** Disable menu items based on rightsMap, and replace parameters with values.  Recursive.
   * @param items MenuItem tree, with placeholders for parameters
   * @param paramMap name-value pairs for parameter replacement, e.g. ':supplierId' -> 'a1b2c3'
   * @param rightsMap map of route paths to rights
   * @param user current user, for evaluating rights
  */
  static processMenuItems(items: MenuItem[], paramMap: { [param: string]: string }, rightsMap: RightsMap, user?: AuthUser): void {
    for (const item of items) {
      const rl = item.routerLink as string[];
      if (rl) {

        // disable the link if the route data rights are not satisfied
        const path = rl.join('/');
        const routeRights = rightsMap[path];
        if (routeRights && !user?.hasRight(...routeRights)) {
          // item.disabled = true;
          // item.styleClass = 'prox-fade';
          item.routerLink = null;
          item.title = "You do not have access to this."
        }

        for (let i = 0; i < rl.length; i++) {
          // replace parameter with value, e.g. ':supplierId' -> 'a1b2c3'
          const val = paramMap[rl[i]];
          if (val) {
            rl[i] = val;
          }
        }
      }
      if (item.items) {
        this.processMenuItems(item.items, paramMap, rightsMap, user);
      }
    }
  }

  /** Open the one submenu section that contains the current route, and close the rest.
   *
   * The manager submenus are accordions ([multiple]="false") whose sections all start collapsed.
   * Collapsed-by-default alone loses your place: after navigating you cannot see which section
   * you are in, or reach its siblings, without reopening it by hand. This restores that without
   * letting the menu run past the fold.
   *
   * Matching is on the longest routerLink that the URL actually contains, as a whole path
   * segment. Longest wins so a short link ('settings') cannot beat a more specific sibling, and
   * the segment boundaries stop 'users' matching 'accounts-for-users'.
   *
   * No match leaves every section closed, unless the caller names a fallback section with
   * fallbackSectionId. A landing page that is not itself in the menu matches nothing, so a
   * manager whose landing page has a most-used section opens that one instead of showing the
   * operator seven closed headings.
   *
   * @param items top-level menu sections
   * @param currentUrl router.url
   * @param fallbackSectionId id of the section to open when the URL matches nothing */
  static expandActiveSection(items: MenuItem[], currentUrl: string, fallbackSectionId?: string): void {
    const url = RouteFns.trimSlashes((currentUrl ?? '').split('?')[0].split('#')[0]);

    let best: MenuItem | undefined;
    let bestLength = -1;
    for (const section of items) {
      const length = RouteFns.deepestMatchLength(section, url);
      if (length > bestLength) {
        bestLength = length;
        best = length >= 0 ? section : undefined;
      }
    }

    if (bestLength < 0 && fallbackSectionId) {
      best = items.find(section => section.id === fallbackSectionId);
    }

    for (const section of items) {
      section.expanded = !!best && section === best;
    }
  }

  /** Longest routerLink under this item that the URL contains, or -1 for no match. */
  private static deepestMatchLength(item: MenuItem, url: string): number {
    let best = -1;
    const rl = item.routerLink as string[] | null;
    if (rl?.length) {
      const path = RouteFns.trimSlashes(rl.join('/'));
      if (path && (url === path || url.endsWith('/' + path) || url.includes('/' + path + '/'))) {
        best = path.length;
      }
    }
    for (const child of item.items ?? []) {
      best = Math.max(best, RouteFns.deepestMatchLength(child, url));
    }
    return best;
  }

  private static trimSlashes(s: string): string {
    return s.replace(/^\/+/, '').replace(/\/+$/, '');
  }

  /** Populate the rightsMap from the route tree data.
   * Recursively converts routes into flat paths, then adds them to rightsMap. 
   * @param parent path under which the routes begin, e.g. '/supplier' 
   * @param routes route tree 
   * @param rightsMap map to be populated with flat path -> array of rights */
  static populateRightsMap(parent: string, routes: Route[], rightsMap: RightsMap): void {
    for (const rt of routes) {
      const path = parent + '/' + rt.path;
      const rights = rt.data?.['rights'];
      if (rights) {
        rightsMap[path] = rights;
      }
      if (rt.children) {
        this.populateRightsMap(path, rt.children, rightsMap);
      }
    }
  }

  /** Get the combined path of the route configs.  
   * This activated route URL, but with the param names instead of values.
   * Example: '//supplier/suppliers/:supplierId/manage/product-types/:productTypeId' */
  static getRouteConfigPath(route: ActivatedRoute | ActivatedRouteSnapshot) {
    return route.pathFromRoot.map(x => x.routeConfig?.path).join('/');
  }

}